import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ENV from 'constants/env';
import payrollnootLogoLg from 'assets/images/payrollnoot-logo-lg.png';
import './login.scss';

const API_BASE = (() => {
    const raw = ENV.BASE_URL || '';
    const trimmed = raw.trim().replace(/\/+$/, '');
    return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
})();

const inviteApi = axios.create({
    baseURL: API_BASE,
    headers: { 'X-API-key': ENV.API_KEY }
});

const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
    'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
    'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

const EMPTY_FIRM = {
    firmName: '', tradeName: '', ein: '', phone: '', firmEmail: '',
    addressLine1: '', addressLine2: '', city: '', state: '', zip: '', country: 'US',
    firstName: '', lastName: '', password: '', confirmPassword: '', acceptTerms: false,
};

/**
 * Invite registration:
 * - company + employer portal → redirect to onboarding URL
 * - accountant → light firm self-onboarding wizard
 * - reseller → set password only (firm already created by SuperAdmin)
 */
const RegisterFromInvite = () => {
    const { type } = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    const [invite, setInvite] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [step, setStep] = useState(0); // accountant: 0 firm, 1 contact, 2 review
    const [form, setForm] = useState(EMPTY_FIRM);

    useEffect(() => {
        if (!token) { setError('Invalid invite link — no token found.'); setLoading(false); return; }
        inviteApi.get(`/invites/validate/${token}`)
            .then(res => {
                const data = res.data;
                setInvite(data);
                setForm(f => ({
                    ...f,
                    firmName: data.companyName || '',
                    firmEmail: data.email || '',
                }));
                setLoading(false);
                if (data.usesEmployerPortal && data.onboardingInviteUrl) {
                    window.location.href = data.onboardingInviteUrl;
                }
            })
            .catch(err => {
                const data = err.response?.data;
                if (data?.code === 'use_employer_portal' && data?.onboardingInviteUrl) {
                    window.location.href = data.onboardingInviteUrl;
                    return;
                }
                setError(data?.message || 'Invalid or expired invite link.');
                setLoading(false);
            });
    }, [token]);

    const inviteType = (invite?.inviteType || type || '').toLowerCase();
    const isAccountant = inviteType === 'accountant';
    const isReseller = inviteType === 'reseller' || inviteType === 'staff';
    const isStaff = inviteType === 'staff';
    const loginPath = isReseller ? '/login/reseller' : isAccountant ? '/login/accountant' : '/login/company';

    const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    const validateFirmStep = () => {
        if (!form.firmName.trim()) return 'Firm legal name is required.';
        if (!form.ein.trim()) return 'Firm EIN is required.';
        if (!form.addressLine1.trim() || !form.city.trim() || !form.state.trim() || !form.zip.trim())
            return 'Firm address (street, city, state, ZIP) is required.';
        return null;
    };

    const validateContactStep = () => {
        if (!form.firstName.trim() || !form.lastName.trim()) return 'Contact name is required.';
        if (form.password.length < 8) return 'Password must be at least 8 characters.';
        if (form.password !== form.confirmPassword) return 'Passwords do not match.';
        if (!form.acceptTerms) return 'You must accept the terms to continue.';
        return null;
    };

    const validateResellerStep = () => {
        if (form.password.length < 8) return 'Password must be at least 8 characters.';
        if (form.password !== form.confirmPassword) return 'Passwords do not match.';
        return null;
    };

    const handleNext = () => {
        setError('');
        if (step === 0) {
            const err = validateFirmStep();
            if (err) return setError(err);
            setStep(1);
            return;
        }
        if (step === 1) {
            const err = validateContactStep();
            if (err) return setError(err);
            setStep(2);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (isAccountant) {
            const e1 = validateFirmStep();
            if (e1) return setError(e1);
            const e2 = validateContactStep();
            if (e2) return setError(e2);
        } else if (isReseller) {
            const err = validateResellerStep();
            if (err) return setError(err);
        } else {
            if (form.password !== form.confirmPassword) return setError('Passwords do not match.');
            if (form.password.length < 8) return setError('Password must be at least 8 characters.');
        }
        setSubmitting(true);
        try {
            await inviteApi.post(`/invites/complete`, {
                token,
                password: form.password,
                firstName: form.firstName || undefined,
                lastName: form.lastName || undefined,
                firmName: form.firmName || undefined,
                tradeName: form.tradeName || undefined,
                ein: form.ein || undefined,
                phone: form.phone || undefined,
                firmEmail: form.firmEmail || undefined,
                addressLine1: form.addressLine1 || undefined,
                addressLine2: form.addressLine2 || undefined,
                city: form.city || undefined,
                state: form.state || undefined,
                zip: form.zip || undefined,
                country: form.country || 'US',
            });
            setSuccess(true);
        } catch (err) {
            const data = err.response?.data;
            if (data?.onboardingInviteUrl) {
                window.location.href = data.onboardingInviteUrl;
                return;
            }
            setError(data?.message || 'Registration failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ color: '#6b7280' }}>Validating invite link...</p>
        </div>
    );

    // Company invites that use employer portal (while redirecting)
    if (invite?.usesEmployerPortal) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                <div style={{ textAlign: 'center', maxWidth: 420 }}>
                    <p style={{ color: '#374151' }}>Redirecting to employer onboarding…</p>
                    {invite.onboardingInviteUrl && (
                        <a href={invite.onboardingInviteUrl} style={{ color: '#1B75F0' }}>Open onboarding portal</a>
                    )}
                </div>
            </div>
        );
    }

    const field = (label, key, opts = {}) => (
        <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>{label}</label>
            {opts.type === 'select' ? (
                <select value={form[key]} onChange={e => setField(key, e.target.value)} required={opts.required}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}>
                    <option value="">Select</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            ) : (
                <input
                    type={opts.type || 'text'}
                    value={form[key]}
                    onChange={e => setField(key, e.target.value)}
                    placeholder={opts.placeholder}
                    required={opts.required}
                    autoComplete={opts.autoComplete || 'off'}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', ...(opts.disabled ? { background: '#f9fafb', color: '#6b7280' } : {}) }}
                    disabled={opts.disabled}
                />
            )}
        </div>
    );

    const title = isStaff
        ? 'Join your reseller team'
        : isReseller
            ? 'Set your reseller password'
            : isAccountant
                ? 'Set up your accounting firm'
                : 'Complete Your Registration';

    const subtitle = invite ? (
        isStaff
            ? <>Team account for <strong>{invite.email}</strong></>
            : isReseller
                ? <>Reseller account for <strong>{invite.email}</strong>{invite.companyName ? <> · <strong>{invite.companyName}</strong></> : null}</>
                : isAccountant
                    ? <>Practice account for <strong>{invite.email}</strong></>
                    : <>Company account for <strong>{invite.email}</strong></>
    ) : null;

    return (
        <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', padding: '40px 36px', width: '100%', maxWidth: isAccountant ? 560 : 440 }}>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <img src={payrollnootLogoLg} alt="PayrollNoot" style={{ height: 48, marginBottom: 16 }} />
                    <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a', margin: 0 }}>{title}</h2>
                    {invite && (
                        <p style={{ color: '#6b7280', marginTop: 8, fontSize: 14 }}>{subtitle}</p>
                    )}
                </div>

                {isAccountant && !success && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 20, justifyContent: 'center' }}>
                        {['Firm', 'Contact', 'Review'].map((label, i) => (
                            <div key={label} style={{
                                padding: '6px 12px', borderRadius: 99, fontSize: 12, fontWeight: 700,
                                background: step === i ? '#EFF6FF' : (step > i ? '#f0fdf4' : '#f3f4f6'),
                                color: step === i ? '#1B75F0' : (step > i ? '#15803d' : '#9ca3af'),
                            }}>{i + 1}. {label}</div>
                        ))}
                    </div>
                )}

                {error && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', color: '#ef4444', fontSize: 14, marginBottom: 20, textAlign: 'center' }}>
                        {error}
                    </div>
                )}

                {success ? (
                    <div style={{ textAlign: 'center' }}>
                        <h3 style={{ fontWeight: 700, color: '#1a1a1a' }}>
                            {isReseller ? 'Password set!' : 'Registration Complete!'}
                        </h3>
                        <p style={{ color: '#6b7280', marginBottom: 24 }}>
                            {isReseller
                                ? 'Your reseller account is ready. You can now log in.'
                                : 'Your account has been created. You can now log in.'}
                        </p>
                        <button type="button" onClick={() => navigate(loginPath)}
                            style={{ background: '#1B75F0', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 32px', fontWeight: 700, fontSize: 15, cursor: 'pointer', width: '100%' }}>
                            Go to Login
                        </button>
                    </div>
                ) : invite && isReseller ? (
                    <form onSubmit={handleSubmit} autoComplete="off">
                        <div style={{
                            background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10,
                            padding: '12px 14px', marginBottom: 18, fontSize: 13, color: '#1e3a8a', lineHeight: 1.5,
                        }}>
                            {isStaff
                                ? <>Create a password for <strong>{invite.email}</strong>. You’ll use it to sign in at /login/reseller with your assigned team permissions.</>
                                : <>Create a password for <strong>{invite.email}</strong>. You’ll use it to sign in at /login/reseller.</>}
                        </div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <div style={{ flex: 1 }}>{field('First name (optional)', 'firstName', { autoComplete: 'given-name' })}</div>
                            <div style={{ flex: 1 }}>{field('Last name (optional)', 'lastName', { autoComplete: 'family-name' })}</div>
                        </div>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>Login email</label>
                            <input type="email" value={invite.email} disabled
                                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#f9fafb', color: '#6b7280', boxSizing: 'border-box' }} />
                        </div>
                        {field('Password', 'password', { type: 'password', required: true, autoComplete: 'new-password', placeholder: 'Minimum 8 characters' })}
                        {field('Confirm password', 'confirmPassword', { type: 'password', required: true, autoComplete: 'new-password' })}
                        <button type="submit" disabled={submitting}
                            style={{ width: '100%', background: '#1B75F0', color: '#fff', border: 'none', borderRadius: 8, padding: '12px', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 4 }}>
                            {submitting ? 'Saving…' : 'Set password & activate'}
                        </button>
                    </form>
                ) : invite && isAccountant ? (
                    <form onSubmit={step === 2 ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }} autoComplete="off">
                        {step === 0 && (
                            <>
                                {field('Firm legal name', 'firmName', { required: true, placeholder: 'Smith & Associates LLC' })}
                                {field('DBA / trade name (optional)', 'tradeName', { placeholder: 'Optional' })}
                                {field('EIN', 'ein', { required: true, placeholder: 'XX-XXXXXXX' })}
                                {field('Firm phone', 'phone', { placeholder: '(555) 555-5555' })}
                                {field('Firm email', 'firmEmail', { type: 'email', placeholder: invite.email })}
                                {field('Street address', 'addressLine1', { required: true })}
                                {field('Address line 2', 'addressLine2')}
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div style={{ flex: 2 }}>{field('City', 'city', { required: true })}</div>
                                    <div style={{ flex: 1 }}>{field('State', 'state', { type: 'select', required: true })}</div>
                                    <div style={{ flex: 1 }}>{field('ZIP', 'zip', { required: true })}</div>
                                </div>
                            </>
                        )}
                        {step === 1 && (
                            <>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div style={{ flex: 1 }}>{field('First name', 'firstName', { required: true, autoComplete: 'given-name' })}</div>
                                    <div style={{ flex: 1 }}>{field('Last name', 'lastName', { required: true, autoComplete: 'family-name' })}</div>
                                </div>
                                <div style={{ marginBottom: 14 }}>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>Login email</label>
                                    <input type="email" value={invite.email} disabled
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#f9fafb', color: '#6b7280', boxSizing: 'border-box' }} />
                                </div>
                                {field('Password', 'password', { type: 'password', required: true, autoComplete: 'new-password', placeholder: 'Minimum 8 characters' })}
                                {field('Confirm password', 'confirmPassword', { type: 'password', required: true, autoComplete: 'new-password' })}
                                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: '#374151', marginBottom: 16 }}>
                                    <input type="checkbox" checked={form.acceptTerms} onChange={e => setField('acceptTerms', e.target.checked)} style={{ marginTop: 3 }} />
                                    <span>I accept the partner terms and confirm this firm information is accurate.</span>
                                </label>
                            </>
                        )}
                        {step === 2 && (
                            <div style={{ fontSize: 14, color: '#374151', marginBottom: 16, lineHeight: 1.6 }}>
                                <p style={{ margin: '0 0 8px' }}><strong>Firm:</strong> {form.firmName}{form.tradeName ? ` (${form.tradeName})` : ''}</p>
                                <p style={{ margin: '0 0 8px' }}><strong>EIN:</strong> {form.ein}</p>
                                <p style={{ margin: '0 0 8px' }}><strong>Address:</strong> {[form.addressLine1, form.city, form.state, form.zip].filter(Boolean).join(', ')}</p>
                                <p style={{ margin: '0 0 8px' }}><strong>Contact:</strong> {form.firstName} {form.lastName} · {invite.email}</p>
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: 10 }}>
                            {step > 0 && (
                                <button type="button" onClick={() => { setError(''); setStep(s => s - 1); }}
                                    style={{ flex: 1, background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, padding: '12px', fontWeight: 700, cursor: 'pointer' }}>
                                    Back
                                </button>
                            )}
                            <button type="submit" disabled={submitting}
                                style={{ flex: 2, background: '#1B75F0', color: '#fff', border: 'none', borderRadius: 8, padding: '12px', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
                                {submitting ? 'Creating…' : (step === 2 ? 'Create firm account' : 'Next')}
                            </button>
                        </div>
                    </form>
                ) : invite ? (
                    <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
                        <p>This company invite should open the employer onboarding portal.</p>
                        <p>Ask your administrator to resend the invite if you do not have a portal link.</p>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default RegisterFromInvite;
